import { useCallback } from 'react';
import type { ChildrenQueryData } from '@atlassian/jira-business-list-handlers';
import { ff } from '@atlassian/jira-feature-flagging';
import {
	ChangeEventTypes,
	type OnChangeCallback,
} from '@atlassian/jira-issue-view-model/src/change-type';
import type { TimelineViewIssuesReturn } from '../../services/get-timeline-view-issues/types';
import { useTimelineViewUpdater as useTimelineViewUpdaterService } from '../../services/timeline-view-updater';

export const useDependencyUpdater = ({
	childrenQueryData,
}: {
	childrenQueryData: ChildrenQueryData<TimelineViewIssuesReturn>;
}) => {
	const { onIssueUpdated, onIssueRemoved } = useTimelineViewUpdaterService({ childrenQueryData });

	const onBentoChange: OnChangeCallback = useCallback(
		(event) => {
			if (event.type === ChangeEventTypes.ISSUE_LINK_DELETED) {
				onIssueUpdated([Number(event.issueId), Number(event.meta.linkedIssueId)]);
			}
			if (event.type === ChangeEventTypes.ISSUE_LINKS_CREATED) {
				onIssueUpdated(
					[Number(event.issueId)].concat(
						event.meta.links.map((link) => Number(link.linkedIssueId)),
					),
				);
			}
			if (event.type === ChangeEventTypes.ISSUE_RELATIONSHIP_UPDATED) {
				const {
					issueId,
					meta: { oldParentIssueId },
				} = event;

				if (ff('jwm.visual-hierarchy-milestone-1')) {
					// oldParentIssueId can be undefined if the issue didn't have a parent before the change
					onIssueRemoved({
						issueId: Number(issueId),
						parentId: oldParentIssueId ?? undefined,
					});
				} else if (oldParentIssueId) {
					onIssueRemoved({ issueId: Number(issueId), parentId: oldParentIssueId });
				}
			}
		},
		[onIssueRemoved, onIssueUpdated],
	);

	const onBentoDelete = useCallback(
		({ issueId, parentId }: { issueId: number; parentId?: number }) => {
			onIssueRemoved({ issueId, parentId });
		},
		[onIssueRemoved],
	);

	return { onBentoChange, onBentoDelete };
};
